/** HTTP client for Umbraco Delivery API v2 quiz documents (`eCEQuizContent`). */
import { getDeliveryContentUrl } from '../config/env';
import { parseQuizDeliveryItem } from '../model/parse-delivery';
import type { QuizConfig } from '../model/types';

/** Builds the Delivery API query string: content type + article URL must match CMS indexing (see README “CMS query”). */
function buildQuery(articleUrl: string): string {
  const params = new URLSearchParams();
  params.append('filter', 'contentTypes:eCEQuizContent');
  params.append('filter', `articleUrl:${articleUrl}`);
  return params.toString();
}

/**
 * Load quiz config from Umbraco Delivery API v2 for this page’s canonical URL.
 *
 * Flow: build URL → GET JSON → validate/normalize via `parseQuizDeliveryItem` → return config or `null`.
 * Any failure (HTTP error, empty `items`, malformed body, or rules in `parse-delivery`) yields `null` so the widget can no-op quietly.
 */
export async function fetchQuizForArticle(articleUrl: string): Promise<QuizConfig | null> {
  const url = `${getDeliveryContentUrl()}?${buildQuery(articleUrl)}`;
  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });

    // Treat missing document and other errors the same: caller sees no quiz (no thrown errors).
    if (res.status === 404) return null;
    if (!res.ok) return null;

    const json: unknown = await res.json();

    // Strict validation (e.g. exactly seven questions) lives in the parser; invalid payloads become null.
    const config = parseQuizDeliveryItem(json);

    // Dev-only hints when the API responded OK but we still have nothing usable (see README troubleshooting).
    if (import.meta.env.DEV) {
      const root =
        json !== null && typeof json === 'object' && !Array.isArray(json)
          ? (json as Record<string, unknown>)
          : null;
      const items = root?.items;
      const n = Array.isArray(items) ? items.length : -1;
      if (n === 0) {
        console.warn(
          '[ece-quiz] Delivery returned 0 items — the `articleUrl` filter must match the CMS `articleURL` value (localhost will not match). Use `?articleUrl=` or `VITE_DEV_ARTICLE_URL`.',
          { articleUrl, requestUrl: url },
        );
      } else if (!config && n > 0) {
        console.warn(
          '[ece-quiz] Delivery returned content but the quiz failed validation (e.g. not exactly 7 in-widget questions or answer labels missing from results).',
        );
      }
    }

    return config;
  } catch {
    // Network failure, CORS, or non-JSON body — same outcome as “no quiz” for embed consumers.
    return null;
  }
}
